/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Stapled (offline) OCSP revocation checking per C2PA §15.9.1 + RFC 6960. A
 * claim generator may staple OCSP responses for the signer + intermediates in
 * the COSE unprotected `rVals` header (`ocspVals`, §14.5.2). This module decodes
 * each response, verifies it was signed by an authorized responder (RFC 6960
 * §4.2.2.2), matches its CertID to a chain certificate, and — for a `revoked`
 * status within the response's validity window relative to the attested
 * time-stamp — reports the credential revoked (`signingCredential.ocsp.revoked`).
 *
 * Only the **revoked** outcome is surfaced (a hard fail); `good`/`unknown`/no-match
 * are treated as "not revoked" (the safe direction, and §15.9 default when no
 * revocation info applies). Online OCSP querying (§15.9.2) is intentionally not
 * implemented (privacy/network — a product decision).
 *
 * Per §15.9.1 "if we cannot interpret the OCSP data, treat it as if it did not
 * exist": any parse/verify failure yields "not revoked".
 */

import { readTlv, readChildren, content, tlvBytes, decodeOid, decodeIntegerDecimal, type Tlv } from './der'
import { parseCertificate, type CertInfo } from './parseCert'
import { verifySignatureWithSpki } from './trust'
import { parseGeneralizedTime } from './parseTimestamp'

const ID_PKIX_OCSP_BASIC = '1.3.6.1.5.5.7.48.1.1'
const ID_KP_OCSP_SIGNING = '1.3.6.1.5.5.7.3.9'
const CRL_REASON_REMOVE_FROM_CRL = 8

const HASH_FOR_OID: Record<string, string> = {
    '1.3.14.3.2.26': 'SHA-1',
    '2.16.840.1.101.3.4.2.1': 'SHA-256',
    '2.16.840.1.101.3.4.2.2': 'SHA-384',
    '2.16.840.1.101.3.4.2.3': 'SHA-512'
}

const oidOf = (u8: Uint8Array, t: Tlv): string => decodeOid(content(u8, t))
const hex = (b: Uint8Array): string => Array.from(b, x => x.toString(16).padStart(2, '0')).join('')

async function digest (alg: string, data: Uint8Array): Promise<Uint8Array> {
    return new Uint8Array(await crypto.subtle.digest(alg, data as unknown as ArrayBuffer))
}

/** The public-key bytes of a SubjectPublicKeyInfo (the BIT STRING value minus the unused-bits octet). */
function spkiKeyBytes (spki: Uint8Array): Uint8Array | null {
    try {
        const seq = readTlv(spki, 0)
        const bitStr = readChildren(spki, seq.contentStart, seq.contentEnd).find(t => t.tag === 0x03)
        if (!bitStr) return null
        return spki.subarray(bitStr.contentStart + 1, bitStr.contentEnd) // skip unused-bits count
    } catch {
        return null
    }
}

interface SingleResponse {
    hashAlg: string
    issuerNameHash: Uint8Array
    issuerKeyHash: Uint8Array
    serial: string
    status: 'good' | 'revoked' | 'unknown'
    revocationReason?: number
    thisUpdate: string | null
    nextUpdate: string | null
}

interface BasicOcsp {
    tbs: Uint8Array
    sigAlgOid: string
    sigAlgParams?: Uint8Array
    signature: Uint8Array
    certs: Uint8Array[]
    producedAt: string | null
    responses: SingleResponse[]
}

/** Parses a DER OCSPResponse into its BasicOCSPResponse, or null if not a successful basic response. */
function parseOcsp (der: Uint8Array): BasicOcsp | null {
    const outer = readTlv(der, 0)
    const outerKids = readChildren(der, outer.contentStart, outer.contentEnd)
    // responseStatus ENUMERATED (0x0a): 0 == successful.
    const statusTlv = outerKids.find(t => t.tag === 0x0a)
    if (!statusTlv || content(der, statusTlv)[0] !== 0) return null
    // responseBytes [0] EXPLICIT → SEQUENCE { responseType OID, response OCTET STRING }.
    const respBytesCtx = outerKids.find(t => t.tag === 0xa0)
    if (!respBytesCtx) return null
    const respBytes = readTlv(der, respBytesCtx.contentStart)
    const rbKids = readChildren(der, respBytes.contentStart, respBytes.contentEnd)
    if (oidOf(der, rbKids[0]) !== ID_PKIX_OCSP_BASIC) return null
    const basicDer = content(der, rbKids[1]) // OCTET STRING containing BasicOCSPResponse

    const basic = readTlv(basicDer, 0)
    const bKids = readChildren(basicDer, basic.contentStart, basic.contentEnd)
    const tbsTlv = bKids[0] // ResponseData SEQUENCE
    const sigAlgTlv = bKids[1] // AlgorithmIdentifier SEQUENCE
    const sigBitStr = bKids[2] // BIT STRING
    const tbs = tlvBytes(basicDer, tbsTlv)
    const sigAlgKids = readChildren(basicDer, sigAlgTlv.contentStart, sigAlgTlv.contentEnd)
    const sigAlgOid = oidOf(basicDer, sigAlgKids[0])
    const sigAlgParams = sigAlgKids[1] ? tlvBytes(basicDer, sigAlgKids[1]) : undefined
    const signature = basicDer.subarray(sigBitStr.contentStart + 1, sigBitStr.contentEnd) // skip unused-bits
    // certs [0] EXPLICIT SEQUENCE OF Certificate — descend into the wrapper SEQUENCE
    // to get each Certificate (the [0] holds one SEQUENCE OF, not the certs directly).
    const certsCtx = bKids.find(t => t.tag === 0xa0)
    let certs: Uint8Array[] = []
    if (certsCtx) {
        const seqOf = readTlv(basicDer, certsCtx.contentStart)
        certs = readChildren(basicDer, seqOf.contentStart, seqOf.contentEnd).filter(t => t.tag === 0x30).map(t => tlvBytes(basicDer, t))
    }

    // ResponseData: [version [0]], responderID ([1]/[2]), producedAt (0x18), responses (0x30 SEQ), [ext [1]].
    const rdKids = readChildren(basicDer, tbsTlv.contentStart, tbsTlv.contentEnd)
    const producedTlv = rdKids.find(t => t.tag === 0x18)
    const producedAt = producedTlv ? parseGeneralizedTime(new TextDecoder().decode(content(basicDer, producedTlv))) : null
    const responsesSeq = rdKids.find(t => t.tag === 0x30) // SEQUENCE OF SingleResponse
    if (!responsesSeq) return null

    const responses: SingleResponse[] = []
    for (const sr of readChildren(basicDer, responsesSeq.contentStart, responsesSeq.contentEnd)) {
        if (sr.tag !== 0x30) continue
        const srKids = readChildren(basicDer, sr.contentStart, sr.contentEnd)
        const certId = srKids[0] // CertID SEQUENCE
        const cidKids = readChildren(basicDer, certId.contentStart, certId.contentEnd)
        const hashAlg = HASH_FOR_OID[oidOf(basicDer, readChildren(basicDer, cidKids[0].contentStart, cidKids[0].contentEnd)[0])]
        const octets = cidKids.filter(t => t.tag === 0x04)
        const serialTlv = cidKids.find(t => t.tag === 0x02)
        if (!hashAlg || octets.length < 2 || !serialTlv) continue
        // certStatus is the element right after certID: good [0] 0x80 (primitive) /
        // revoked [1] 0xa1 (constructed RevokedInfo) / unknown [2] 0x82. Read it
        // positionally — the context-tag numbers collide with nextUpdate [0] 0xa0
        // and singleExtensions [1] 0xa1, so a tag-only scan is ambiguous.
        const statusCtx = srKids[1]
        let status: SingleResponse['status'] = 'unknown'
        let revocationReason: number | undefined
        if (statusCtx) {
            const t = statusCtx.tag
            if (t === 0x80) status = 'good'
            else if (t === 0xa1) {
                status = 'revoked'
                // RevokedInfo: revocationTime (0x18), revocationReason [0] EXPLICIT ENUMERATED.
                const riKids = readChildren(basicDer, statusCtx.contentStart, statusCtx.contentEnd)
                const reasonCtx = riKids.find(k => k.tag === 0xa0)
                if (reasonCtx) revocationReason = content(basicDer, readTlv(basicDer, reasonCtx.contentStart))[0]
            } else status = 'unknown'
        }
        const times = srKids.filter(t => t.tag === 0x18)
        const thisUpdate = times[0] ? parseGeneralizedTime(new TextDecoder().decode(content(basicDer, times[0]))) : null
        const nextCtx = srKids.find(t => t.tag === 0xa0)
        const nextUpdate = nextCtx ? parseGeneralizedTime(new TextDecoder().decode(content(basicDer, readTlv(basicDer, nextCtx.contentStart)))) : null

        responses.push({
            hashAlg,
            issuerNameHash: content(basicDer, octets[0]),
            issuerKeyHash: content(basicDer, octets[1]),
            serial: decodeIntegerDecimal(content(basicDer, serialTlv)),
            status,
            revocationReason,
            thisUpdate,
            nextUpdate
        })
    }
    return { tbs, sigAlgOid, sigAlgParams, signature, certs, producedAt, responses }
}

/** Whether a SingleResponse's CertID identifies `cert` issued by `issuer`. */
async function certIdMatches (sr: SingleResponse, cert: CertInfo, issuer: CertInfo): Promise<boolean> {
    if (sr.serial !== cert.serialNumber) return false
    const keyBytes = spkiKeyBytes(issuer.spki)
    if (!keyBytes) return false
    if (hex(await digest(sr.hashAlg, issuer.subjectDer)) !== hex(sr.issuerNameHash)) return false
    if (hex(await digest(sr.hashAlg, keyBytes)) !== hex(sr.issuerKeyHash)) return false
    return true
}

/**
 * Identifies + verifies the OCSP responder (RFC 6960 §4.2.2.2): the response's
 * signature must verify under either the issuer's key (the CA is the responder)
 * or an embedded delegate cert that the issuer signed and that carries the
 * id-kp-OCSPSigning EKU. Returns whether an authorized responder signed it.
 */
async function responderAuthorized (b: BasicOcsp, issuer: CertInfo): Promise<boolean> {
    // (a) the issuing CA signs its own OCSP responses.
    if (await verifySignatureWithSpki(issuer.spki, b.sigAlgOid, b.sigAlgParams, b.signature, b.tbs)) return true
    // (b) a delegate responder cert embedded in the response, issued by the CA, with the OCSP-signing EKU.
    for (const der of b.certs) {
        let rc: CertInfo
        try { rc = parseCertificate(der) } catch { continue }
        if (!rc.eku.includes(ID_KP_OCSP_SIGNING)) continue
        if (!(await verifySignatureWithSpki(rc.spki, b.sigAlgOid, b.sigAlgParams, b.signature, b.tbs))) continue
        // The delegate must itself be issued by the CA whose certs it vouches for.
        if (await verifySignatureWithSpki(issuer.spki, rc.signatureAlgOid, rc.sigAlgParams, rc.signature, rc.tbs)) return true
    }
    return false
}

/** True if the attested time falls in the response's validity window (§15.9.1). */
function withinWindow (sr: SingleResponse, producedAt: string | null, attestedTime: string, nowIso: string): boolean {
    // TS-2: compare instants as epoch-ms, not lexically. thisUpdate/nextUpdate/
    // attestedTime are `…+00:00` (from parseGeneralizedTime) while nowIso is `….000Z`;
    // a lexical `<`/`<=` diverges when two instants share the same second-resolution
    // prefix. Date.parse normalizes both ISO-8601 forms to the same numeric instant.
    const ms = (s: string | null | undefined): number => (s ? Date.parse(s) : NaN)
    if (!sr.thisUpdate) return false
    const thisUpd = ms(sr.thisUpdate)
    const now = ms(nowIso)
    const attested = ms(attestedTime)
    if (Number.isNaN(thisUpd) || Number.isNaN(now) || Number.isNaN(attested)) return false
    if (now < thisUpd) return false // current time earlier than thisUpdate
    if (attested < thisUpd) return true
    // (thisUpdate, nextUpdate], or (thisUpdate, producedAt + 24h] when nextUpdate is absent.
    let upper = ms(sr.nextUpdate)
    if (Number.isNaN(upper) && producedAt) {
        const p = ms(producedAt)
        if (!Number.isNaN(p)) upper = p + 24 * 3600 * 1000
    }
    return !Number.isNaN(upper) && attested <= upper
}

export type OcspOutcome = 'revoked' | 'notRevoked' | 'none'

/**
 * Evaluates stapled OCSP responses against a certificate chain (leaf first).
 * `attestedTime` is the trusted time-stamp genTime (§15.9.1 requires a valid
 * time-stamp); when null, the offline check cannot establish revocation and
 * returns `none`. Returns `revoked` if any chain cert is revoked, else
 * `notRevoked` if a `good` response matched, else `none`.
 */
export async function checkStapledOcsp (ocspDers: Uint8Array[], chain: CertInfo[], attestedTime: string | null, nowIso: string): Promise<OcspOutcome> {
    if (attestedTime == null) return 'none' // §15.9.1 precondition: needs an attested time
    let sawGood = false
    for (const der of ocspDers) {
        let b: BasicOcsp | null
        try { b = parseOcsp(der) } catch { b = null }
        if (!b || b.responses.length === 0) continue
        for (const sr of b.responses) {
            // Match the response to a (cert, issuer) pair in the chain.
            for (let i = 0; i + 1 < chain.length; i++) {
                const cert = chain[i]; const issuer = chain[i + 1]
                if (!(await certIdMatches(sr, cert, issuer))) continue
                if (!(await responderAuthorized(b, issuer))) continue
                if (!withinWindow(sr, b.producedAt, attestedTime, nowIso)) continue
                if (sr.status === 'revoked' && sr.revocationReason !== CRL_REASON_REMOVE_FROM_CRL) return 'revoked'
                if (sr.status === 'good' || sr.revocationReason === CRL_REASON_REMOVE_FROM_CRL) sawGood = true
            }
        }
    }
    return sawGood ? 'notRevoked' : 'none'
}

/** Extracts the DER OCSP responses stapled in a COSE `rVals` unprotected header (`ocspVals`). */
export function ocspValsFromHeader (rVals: unknown): Uint8Array[] {
    const vals = (rVals as { ocspVals?: unknown } | undefined)?.ocspVals
    if (!Array.isArray(vals)) return []
    return vals.filter((v): v is Uint8Array => v instanceof Uint8Array)
}
